<script setup>
import {businessContact} from '@/utils/contact'
import {onMounted, ref} from 'vue'
import axios from 'axios'
import {useHead} from '@vueuse/head'
import HomeHeader from '@/components/HomeHeader.vue'
import Footer from '@/components/Footer.vue'
import VideoCard from '@/components/VideoCard.vue'
import MarketingInquiryForm from '@/components/MarketingInquiryForm.vue'
import {useI18n} from '@/i18n'

const {t} = useI18n()
const projects = ref([])
const videos = ref([])
const heroVideo = ref(null)
const videoPlaying = ref(true)

const solutionImages = [
  '/images/engineering/slider_1.jpg',
  '/images/mining/products1_22356.jpg',
  '/images/mining/products3_22362.jpg',
  '/media/video/covers/steel-school.jpg'
]

const factoryImages = [
  '/images/mining/index_factory2_22232.jpg',
  '/images/mining/index_factory3_22233.jpg',
  '/images/mining/index_factory4_22234.jpg',
  '/images/mining/index_factory1_22231.jpg'
]

function itemsFrom(response) {
  return response?.data?.results || response?.data || []
}

function detailPath(type, item) {
  const slug = item.site || item.project_title || item.blog_title || item.id
  return `/${type}/${encodeURIComponent(slug)}`
}

function handleImageError(event, fallback = '/images/engineering/slider_1.jpg') {
  if (event.target.dataset.fallbackApplied) return
  event.target.dataset.fallbackApplied = 'true'
  event.target.src = fallback
}

function toggleHeroVideo() {
  if (!heroVideo.value) return
  if (heroVideo.value.paused) {
    heroVideo.value.play()
  } else {
    heroVideo.value.pause()
  }
}

onMounted(async () => {
  const [projectResult, videoResult] = await Promise.allSettled([
    axios.get('/api/projects/'),
    axios.get('/api/videos/', {params: {is_featured: true}})
  ])

  if (projectResult.status === 'fulfilled') projects.value = itemsFrom(projectResult.value).slice(0, 3)
  if (videoResult.status === 'fulfilled') videos.value = itemsFrom(videoResult.value).slice(0, 3)
})

useHead({
  title: () => t('marketing.meta.homeTitle'),
  meta: [{name: 'description', content: () => t('marketing.meta.homeDescription')}]
})
</script>

<template>
  <div class="page mkt-page">
    <HomeHeader/>

    <main>
      <section class="mkt-hero">
        <video ref="heroVideo" class="mkt-hero-video" autoplay muted loop playsinline preload="metadata"
               poster="/images/engineering/slider_1.jpg" @play="videoPlaying = true" @pause="videoPlaying = false">
          <source src="/images/2023-TPAHOUSING-04.mp4" type="video/mp4">
        </video>
        <div class="mkt-hero-shade"></div>
        <div class="mkt-shell mkt-hero-inner">
          <div class="mkt-hero-copy">
            <span class="mkt-eyebrow is-light">{{ t('marketing.home.hero.eyebrow') }}</span>
            <h1>{{ t('marketing.home.hero.title') }}</h1>
            <p>{{ t('marketing.home.hero.text') }}</p>
            <div class="mkt-hero-actions">
              <a class="mkt-button mkt-button-accent" href="#project-inquiry">
                {{ t('marketing.shared.quote') }}<i class="mdi mdi-arrow-right" aria-hidden="true"></i>
              </a>
              <a class="mkt-button mkt-button-ghost" href="/projects">
                {{ t('marketing.shared.viewProjects') }}
              </a>
            </div>
            <p class="mkt-hero-note"><i class="mdi mdi-check-decagram" aria-hidden="true"></i>{{ t('marketing.home.hero.note') }}</p>
          </div>
          <button class="mkt-video-toggle" type="button" @click="toggleHeroVideo"
                  :aria-label="videoPlaying ? 'Pause background video' : 'Play background video'">
            <i class="mdi" :class="videoPlaying ? 'mdi-pause' : 'mdi-play'" aria-hidden="true"></i>
          </button>
        </div>
      </section>

      <section class="mkt-stats" aria-label="Company facts">
        <div class="mkt-shell mkt-stats-grid">
          <div v-for="stat in t('marketing.home.stats')" :key="stat.label" class="mkt-stat">
            <strong>{{ stat.value }}</strong>
            <span>{{ stat.label }}</span>
          </div>
        </div>
      </section>

      <section class="mkt-section mkt-solutions">
        <div class="mkt-shell">
          <div class="mkt-section-heading">
            <div>
              <span class="mkt-eyebrow">{{ t('marketing.home.solutions.eyebrow') }}</span>
              <h2>{{ t('marketing.home.solutions.title') }}</h2>
            </div>
            <p>{{ t('marketing.home.solutions.intro') }}</p>
          </div>
          <div class="mkt-solution-grid">
            <article v-for="(solution, index) in t('marketing.home.solutions.items')" :key="solution.title" class="mkt-solution-card">
              <a class="mkt-solution-image" href="/products">
                <img :src="solutionImages[index]" :alt="solution.title" loading="lazy" @error="handleImageError">
                <span>{{ String(index + 1).padStart(2, '0') }}</span>
              </a>
              <div>
                <h3><a href="/products">{{ solution.title }}</a></h3>
                <p>{{ solution.text }}</p>
                <a class="mkt-text-link" href="/products">{{ t('marketing.shared.viewProducts') }}<i class="mdi mdi-arrow-right"></i></a>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section class="mkt-section mkt-advantages">
        <div class="mkt-shell mkt-advantages-layout">
          <div class="mkt-advantages-intro">
            <span class="mkt-eyebrow is-light">{{ t('marketing.home.advantages.eyebrow') }}</span>
            <h2>{{ t('marketing.home.advantages.title') }}</h2>
            <p>{{ t('marketing.home.advantages.intro') }}</p>
            <a class="mkt-button mkt-button-outline-light" href="/about">{{ t('marketing.shared.discoverAbout') }}</a>
          </div>
          <div class="mkt-advantage-grid">
            <article v-for="advantage in t('marketing.home.advantages.items')" :key="advantage.title">
              <i class="mdi" :class="advantage.icon" aria-hidden="true"></i>
              <h3>{{ advantage.title }}</h3>
              <p>{{ advantage.text }}</p>
            </article>
          </div>
        </div>
      </section>

      <section v-if="projects.length" class="mkt-section mkt-projects-section">
        <div class="mkt-shell">
          <div class="mkt-section-heading is-action">
            <div>
              <span class="mkt-eyebrow">{{ t('marketing.home.projects.eyebrow') }}</span>
              <h2>{{ t('marketing.home.projects.title') }}</h2>
              <p>{{ t('marketing.home.projects.intro') }}</p>
            </div>
            <a class="mkt-button mkt-button-outline" href="/projects">{{ t('marketing.shared.viewProjects') }}</a>
          </div>
          <div class="mkt-project-grid">
            <article v-for="(project, index) in projects" :key="project.id || project.project_title"
                     class="mkt-project-card" :class="{featured: index === 0}">
              <a :href="detailPath('projects', project)">
                <img :src="project.image_1" :alt="project.project_title || t('marketing.shared.projectFallback')"
                     loading="lazy" @error="handleImageError">
                <span class="mkt-project-overlay"></span>
                <span class="mkt-project-content">
                  <small>{{ project.category_id?.category || t('nav.projects') }}</small>
                  <strong>{{ project.project_title || t('marketing.shared.projectFallback') }}</strong>
                  <i class="mdi mdi-arrow-top-right" aria-hidden="true"></i>
                </span>
              </a>
            </article>
          </div>
        </div>
      </section>

      <section class="mkt-section mkt-factory-section">
        <div class="mkt-shell mkt-factory-layout">
          <div class="mkt-factory-media">
            <img src="/images/mining/index_factory1_22231.jpg" alt="TPA Housing steel fabrication workshop" loading="lazy">
            <div class="mkt-factory-metric"><strong>20,000+</strong><span>{{ t('marketing.home.factory.metric') }}</span></div>
          </div>
          <div class="mkt-factory-copy">
            <span class="mkt-eyebrow">{{ t('marketing.home.factory.eyebrow') }}</span>
            <h2>{{ t('marketing.home.factory.title') }}</h2>
            <p>{{ t('marketing.home.factory.text') }}</p>
            <ul class="mkt-check-list">
              <li v-for="point in t('marketing.home.factory.points')" :key="point"><i class="mdi mdi-check" aria-hidden="true"></i>{{ point }}</li>
            </ul>
            <a class="mkt-text-link" href="/about">{{ t('marketing.home.factory.link') }}<i class="mdi mdi-arrow-right"></i></a>
          </div>
        </div>
        <div class="mkt-shell mkt-production-strip">
          <div class="mkt-production-title">
            <span>{{ t('marketing.home.factory.processTitle') }}</span>
          </div>
          <article v-for="(process, index) in t('marketing.home.factory.processes')" :key="process">
            <img :src="factoryImages[index]" :alt="process" loading="lazy">
            <span>{{ String(index + 1).padStart(2, '0') }}</span>
            <h3>{{ process }}</h3>
          </article>
        </div>
      </section>

      <section class="mkt-section mkt-process-section">
        <div class="mkt-shell">
          <div class="mkt-section-heading">
            <div>
              <span class="mkt-eyebrow">{{ t('marketing.home.process.eyebrow') }}</span>
              <h2>{{ t('marketing.home.process.title') }}</h2>
            </div>
            <p>{{ t('marketing.home.process.intro') }}</p>
          </div>
          <div class="mkt-process-grid">
            <article v-for="step in t('marketing.home.process.steps')" :key="step.number">
              <span>{{ step.number }}</span>
              <h3>{{ step.title }}</h3>
              <p>{{ step.text }}</p>
            </article>
          </div>
        </div>
      </section>

      <section v-if="videos.length" class="mkt-section mkt-videos-section">
        <div class="mkt-shell">
          <div class="mkt-section-heading is-action">
            <div>
              <span class="mkt-eyebrow">{{ t('marketing.home.videos.eyebrow') }}</span>
              <h2>{{ t('marketing.home.videos.title') }}</h2>
              <p>{{ t('marketing.home.videos.intro') }}</p>
            </div>
            <a class="mkt-button mkt-button-outline" href="/videos">{{ t('marketing.shared.allVideos') }}</a>
          </div>
          <div class="video-grid mkt-video-grid">
            <VideoCard v-for="video in videos" :key="video.id" :video="video"/>
          </div>
        </div>
      </section>

      <section class="mkt-testimonial-band">
        <div class="mkt-shell mkt-testimonial-inner">
          <div class="mkt-testimonial-mark">“</div>
          <div>
            <span class="mkt-eyebrow is-light">{{ t('marketing.home.testimonial.eyebrow') }}</span>
            <blockquote>{{ t('marketing.home.testimonial.quote') }}</blockquote>
            <p><strong>{{ t('marketing.home.testimonial.name') }}</strong><span>{{ t('marketing.home.testimonial.role') }}</span></p>
          </div>
          <img src="/images/about_1.jpg" alt="TPA Housing factory" loading="lazy">
        </div>
      </section>

      <section class="mkt-section mkt-faq-section">
        <div class="mkt-shell mkt-faq-layout">
          <div class="mkt-faq-intro">
            <span class="mkt-eyebrow">{{ t('marketing.home.faq.eyebrow') }}</span>
            <h2>{{ t('marketing.home.faq.title') }}</h2>
            <p>{{ t('marketing.home.faq.intro') }}</p>
            <a class="mkt-text-link" href="/contact">{{ t('marketing.shared.contact') }}<i class="mdi mdi-arrow-right"></i></a>
          </div>
          <div class="mkt-faq-list">
            <details v-for="(item, index) in t('marketing.home.faq.items')" :key="item.q" :open="index === 0">
              <summary><span>{{ String(index + 1).padStart(2, '0') }}</span>{{ item.q }}<i class="mdi mdi-plus"></i></summary>
              <p>{{ item.a }}</p>
            </details>
          </div>
        </div>
      </section>

      <section id="project-inquiry" class="mkt-inquiry-section">
        <div class="mkt-shell mkt-inquiry-layout">
          <div class="mkt-inquiry-copy">
            <span class="mkt-eyebrow is-light">{{ t('marketing.home.inquiry.eyebrow') }}</span>
            <h2>{{ t('marketing.home.inquiry.title') }}</h2>
            <p>{{ t('marketing.home.inquiry.text') }}</p>
            <ul>
              <li v-for="benefit in t('marketing.home.inquiry.benefits')" :key="benefit"><i class="mdi mdi-check-circle-outline"></i>{{ benefit }}</li>
            </ul>
            <div class="mkt-direct-contact">
              <span>{{ t('marketing.home.inquiry.direct') }}</span>
              <a :href="businessContact.whatsappHref" target="_blank" rel="noopener"><i class="mdi mdi-whatsapp"></i><bdi>{{ businessContact.phone }}</bdi></a>
            </div>
          </div>
          <MarketingInquiryForm/>
        </div>
      </section>
    </main>

    <Footer/>
  </div>
</template>
